<script lang="ts">
	import CalendarIcon from "@lucide/svelte/icons/calendar";
	import BedDoubleIcon from "@lucide/svelte/icons/bed-double";
	import BathIcon from "@lucide/svelte/icons/bath";
	import LandPlotIcon from "@lucide/svelte/icons/land-plot";
	import { formatCurrency } from "#lib/shared/formatters.js";
	import { cloudinaryFetch } from "#lib/shared/cloudinary.js";
	import { preloadData } from "$app/navigation";
	import type { OnMarketUnitEntity } from "#backend/modules/buildium-listings/buildium-listings.schemas.js";

	let { units }: { units: OnMarketUnitEntity[] } = $props();

	const preloadedSlugs = new Set<string>();

	function handlePreload(slug: string) {
		if (preloadedSlugs.has(slug)) return;
		preloadedSlugs.add(slug);
		preloadData(`/for-rent/${slug}`);
	}
</script>

<section id="availability" class="bg-[#f5f5f5] py-16">
	<div class="container mx-auto px-4">
		<h2 class="mb-6 text-center text-4xl font-medium text-slate-700">AVAILABLE UNITS</h2>
		<hr class="border-primary mx-auto mb-4 w-[100px] border-2" />

		<div class="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
			{#if units.length === 0}
				<div class="text-muted-foreground col-span-4 text-center">
					No units available at this time.
				</div>
			{:else}
				{#each units as item}
					<a
						href="/for-rent/{item.listing.slug}"
						class="group block hover:no-underline"
						onmouseenter={() => handlePreload(item.listing.slug)}
					>
						<div
							class="group-hover:border-primary flex flex-col rounded border border-gray-200 bg-white transition-all duration-300 ease-in-out group-hover:scale-105 group-hover:shadow-2xl"
						>
							<div
								class="flex h-48 w-full items-center justify-center overflow-hidden rounded-t shadow-xl"
							>
								{#if item.unit.files && item.unit.files.length > 0}
									<img
										src={cloudinaryFetch(item.unit.files[0]?.url, {
											width: 400,
											height: 300,
											crop: "fill"
										})}
										alt="Photo of unit at {item.unit.address.addressLine1}"
										class="h-full w-full object-cover object-center transition-transform duration-500 ease-in-out group-hover:scale-110"
										width="400"
										height="300"
										loading="lazy"
									/>
								{:else}
									<div
										class="flex h-full w-full items-center justify-center bg-gray-200 text-sm text-gray-500"
									>
										Photo Not Available
									</div>
								{/if}
							</div>
							<div class="p-4">
								<div class="mb-2 flex items-start justify-between">
									<span
										class="group-hover:text-primary text-lg font-bold transition-colors duration-300"
									>
										{formatCurrency(item.listing.rent)}/mo
									</span>
									{#if item.listing.rent && item.listing.rent > 0}
										<span class="text-muted-foreground text-sm">
											{formatCurrency(item.listing.deposit)} deposit
										</span>
									{/if}
								</div>

								<div class="flex flex-col items-start">
									<span class="mb-1 font-semibold"
										>{item.unit.address.addressLine1}</span
									>

									{#if item.unit.address.addressLine2}
										<span class="text-sm text-neutral-800/70">
											{item.unit.address.addressLine2}
										</span>
									{/if}

									<span class="text-sm text-neutral-800/70">
										{item.unit.address.city}, {item.unit.address.state}
										{item.unit.address.postalCode}
									</span>
								</div>

								<hr class="my-3 border-gray-200" />

								<div class="text-muted-foreground mt-2 flex flex-row gap-3 text-sm">
									{#if item.unit.unitBedrooms}
										<div class="flex flex-row items-center gap-1">
											<BedDoubleIcon class="h-4 w-4" />
											<span>{item.unit.unitBedrooms.label}</span>
										</div>
									{/if}
									{#if item.unit.unitBathrooms}
										<div class="flex flex-row items-center gap-1">
											<BathIcon class="h-4 w-4" />
											<span>{item.unit.unitBathrooms.label}</span>
										</div>
									{/if}
									{#if item.unit.unitSize}
										<div class="flex flex-row items-center gap-1">
											<LandPlotIcon class="h-4 w-4" />
											<span>{item.unit.unitSize?.toLocaleString()} sf</span>
										</div>
									{/if}
								</div>

								{#if item.unit}
									<div
										class="text-muted-foreground mt-3 flex items-center text-sm"
									>
										<CalendarIcon class="mr-1 h-4 w-4" />
										<span>
											Available {item.listing.availableDate.toLocaleDateString()}
										</span>
									</div>
								{/if}
							</div>
						</div>
					</a>
				{/each}
			{/if}
		</div>
	</div>
</section>
